import {
  Accordion,
  Box,
  Button,
  Link,
  Spinner,
  Text,
} from '@nimbus-ds/components';
import { CheckCircleIcon, CreditCardIcon } from '@nimbus-ds/icons';
import { OnboardingTask } from '@tiendanube/common';
import { useTranslationDashboard } from 'domains/Dashboard/hooks';
import useOnboardingTasks from 'domains/Dashboard/pages/DashboardPage/components/OnboardingTasks/useOnboardingTasks';
import { useNuvemPagoOnboardingConfig } from '../../hooks/useNuvemPagoOnboardingConfig';
import { useNuvemPagoOnboardingRules } from '../../hooks/useNuvemPagoOnboardingRules';

interface NuvemPagoOnboardingTaskComponentProps {
  task: OnboardingTask;
  index: number;
}

function NuvemPagoOnboardingTask({
  task,
  index,
}: NuvemPagoOnboardingTaskComponentProps): JSX.Element | null {
  const {
    handleNuvemPagoOnboarding,
    handleReviewClick,
    isLoadingPayments,
    isProjectShiva,
    isBoldProject,
    storeCountry,
  } = useNuvemPagoOnboardingConfig();

  const { updateOnboardingTaskSetupPayments } = useOnboardingTasks();

  const {
    variant,
    title,
    description,
    primaryButtonText,
    secondaryButtonText,
    primaryButtonUrl,
    secondaryButtonUrl,
    primaryButtonTracking,
    secondaryButtonTracking,
  } = useNuvemPagoOnboardingRules();

  const t = useTranslationDashboard();

  const handlePrimaryClick = async () => {
    updateOnboardingTaskSetupPayments();
    if (variant === 'C') {
      handleReviewClick();
      return;
    }

    await handleNuvemPagoOnboarding({
      url: primaryButtonUrl,
      isSecondaryLink: false,
      storeCountry: storeCountry as string,
      isProjectShiva,
      isBoldProject,
    });
    primaryButtonTracking?.();
  };

  const handleSecondaryClick = async () => {
    const config = {
      url: secondaryButtonUrl || '',
      isSecondaryLink: true,
      storeCountry: storeCountry as string,
      isProjectShiva,
      isBoldProject,
    };

    await handleNuvemPagoOnboarding(config);
    updateOnboardingTaskSetupPayments();
    secondaryButtonTracking?.();
  };

  return (
    <Accordion.Item key={task.name} index={`${index}`}>
      <Accordion.Header borderTop="base">
        <Box width="100%" display="flex" alignItems="center">
          <Box>
            <Box
              backgroundColor={
                task.completed ? 'success-surface' : 'primary-surface'
              }
              color={task.completed ? 'success-textLow' : 'primary-textLow'}
              borderRadius="2"
              height="28px"
              width="28px"
              marginRight="2"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {task.completed ? (
                <CheckCircleIcon color="currentColor" />
              ) : (
                <CreditCardIcon />
              )}
            </Box>
          </Box>
          <Text fontWeight="medium" color="neutral-textHigh">
            {task.completed ? <s>{t(title)}</s> : t(title)}
          </Text>
        </Box>
      </Accordion.Header>
      <Accordion.Body padding="none">
        <Box width="85%" paddingLeft="12" paddingBottom="4">
          <Text fontSize="caption" textAlign="left">
            {t(description)}
          </Text>
          <Box
            paddingTop="1-5"
            display="flex"
            justifyContent="flex-start"
            flexWrap="wrap"
            gap="2"
          >
            <Box marginRight="4">
              <Button
                appearance="primary"
                onClick={handlePrimaryClick}
                disabled={isLoadingPayments}
              >
                {t(primaryButtonText)}
              </Button>
            </Box>
            {!!secondaryButtonUrl && !!secondaryButtonText && (
              <Link
                fontSize="caption"
                textDecoration="none"
                appearance="primary"
                as="button"
                disabled={isLoadingPayments}
                onClick={handleSecondaryClick}
              >
                {t(secondaryButtonText)}
                {isLoadingPayments && (
                  <Spinner color="currentColor" size={10} />
                )}
              </Link>
            )}
          </Box>
        </Box>
      </Accordion.Body>
    </Accordion.Item>
  );
}

export default NuvemPagoOnboardingTask;
